import { enumCallApplicationType } from '@ingenico/api-sdk-device-management-private';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUserDateTimeFormat, useCurrentUserTimezone } from '@ingenico/react-section-utils';
import { CoreStatus } from '@ingenico/react-ui/core-status';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { ListViewItem } from '@ingenico/react-ui/datagrid';
import { getDeviceCallLogsMapperOfStatus } from '@ingenico/react-ui/device-changes-call-logs-section';
import { OverviewChipArray } from '@ingenico/react-ui/overview-tab';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { camelCase, toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseLogicalDeviceHistoryTabColumns } from '../components/logical-device-history-tab/logicalDeviceHistoryTab.model';
import styles from '../components/logical-device-history-tab/logicalDeviceHistoryTab.module.scss';
import type { CallStatusResourceResponseApiFront } from '../model/deviceCallLogs.model';

dayjs.extend(utc);

export function useLogicalDeviceHistoryTabColumns(): UseLogicalDeviceHistoryTabColumns {
  const { t } = useTranslation([
    ALL_LNGS_NAMESPACES.TASKS,
    ALL_LNGS_NAMESPACES.CALL_LOGS,
    ALL_LNGS_NAMESPACES.COMMON,
    ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
  ]);

  const currentUserDateTimeFormat = useCurrentUserDateTimeFormat();
  const timezone = useCurrentUserTimezone();
  const { getUserDateAndTimeFormatWithTimezone } = useUserDateTimeFormat(currentUserDateTimeFormat);

  const mapperOfStatus = useMemo(() => getDeviceCallLogsMapperOfStatus(), []);
  const getTaskStatusLabel = useCallback(
    (labelKey: string, value?: string) => {
      return t(labelKey, { ns: ALL_LNGS_NAMESPACES.TASKS }) || toString(value)?.toUpperCase();
    },
    [t],
  );

  const renderStatus = useCallback(
    (params: GridRenderCellParams): ReactNode => {
      const { labelKey, status } = mapperOfStatus[toString(params?.value).toUpperCase()];
      const statusLabel = getTaskStatusLabel(labelKey, toString(params?.value));
      return (
        <CoreStatus
          label={statusLabel}
          status={status}
          transparentBackground
          customColorsClassNames={{ label: styles.statusLabel }}
        />
      );
    },
    [getTaskStatusLabel, mapperOfStatus],
  );

  const renderApplicationCell = useCallback(
    (params: GridRenderCellParams): ReactNode => {
      const styleChip = params?.value === enumCallApplicationType.DOWNLOAD ? styles.download : styles.helpDesk;
      return (
        <OverviewChipArray
          className={`${styles.coreChipDevice} ${styleChip}`}
          labels={[
            toString(
              t(`callsFields.actions.${camelCase(params?.value as string)}`, {
                ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
              }),
            ),
          ]}
        />
      );
    },
    [t],
  );

  const renderCellValue = useCallback(
    (params: GridRenderCellParams): ReactNode => <ListViewItem primaryText={params.value as string} />,
    [],
  );
  const renderTranslatedStatusSummary = useCallback(
    ({ value }: GridRenderCellParams): ReactNode => {
      const statusSummary = camelCase(toString(value));
      return (
        <ListViewItem
          primaryText={t(`values.callReason.${statusSummary}`, {
            ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
            defaultValue: statusSummary,
          })}
        />
      );
    },
    [t],
  );

  const renderCellDateFormatter = useCallback(
    (params: GridRenderCellParams<CallStatusResourceResponseApiFront, string>): ReactNode => {
      const [date, time] = getUserDateAndTimeFormatWithTimezone({
        instant: params?.value,
        timezone,
      });
      return (
        <div>
          <CoreTypography variant={CoreTypographyVariant.Body02Medium}>{date}</CoreTypography>
          <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{time}</CoreTypography>
        </div>
      );
    },
    [getUserDateAndTimeFormatWithTimezone, timezone],
  );

  const renderCellDurationValue = useCallback(
    (
      params: GridRenderCellParams<CallStatusResourceResponseApiFront, CallStatusResourceResponseApiFront>,
    ): ReactNode => {
      const milliseconds = Number(params?.value) || 0;
      const formatted = dayjs.utc(milliseconds).format('HH:mm:ss');
      return <ListViewItem primaryText={formatted} />;
    },
    [],
  );

  const DEVICE_HISTORY_COLUMNS = useMemo<GridColDef[]>(
    () => [
      {
        field: 'callDateTime',
        headerName: t('callsFields.callDate', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellDateFormatter,
        filterable: false,
      },
      {
        field: 'callDuration',
        headerName: t('callsFields.duration', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        filterable: false,
        flex: 1,
        sortable: false,
        renderCell: renderCellDurationValue,
      },
      {
        field: 'serialNumber',
        headerName: t('callsFields.serialNumber', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'application',
        headerName: t('callsFields.application', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        sortable: false,
        renderCell: renderApplicationCell,
        filterable: false,
      },
      {
        field: 'statusSummary',
        headerName: t('callsFields.summary', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        sortable: false,
        renderCell: renderTranslatedStatusSummary,
        filterable: false,
      },
      {
        field: 'status',
        headerName: t('callsFields.status', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderStatus,
        filterable: false,
      },
    ],
    [
      renderApplicationCell,
      renderCellDateFormatter,
      renderCellDurationValue,
      renderCellValue,
      renderStatus,
      renderTranslatedStatusSummary,
      t,
    ],
  );

  const defaultAvailableColumns = useMemo(
    () => DEVICE_HISTORY_COLUMNS.map((col) => col.field),
    [DEVICE_HISTORY_COLUMNS],
  );

  return {
    columns: DEVICE_HISTORY_COLUMNS,
    defaultAvailableColumns,
  };
}
